/**
 * The export menu items every surface shares.
 *
 * Built here rather than inline so the three callers cannot drift on the parts
 * that are easy to get subtly wrong: the filename sanitising, the error text,
 * and above all the popup-blocker dance in the Drive item. That last one is the
 * reason this file exists — `window.open` after an `await` is blocked, so the
 * tab has to be opened synchronously inside the click and pointed at the file
 * afterwards. There should be exactly one copy of that.
 */

import { api, type DocumentExportDriveRequest } from './api-client';
import { downloadBlob, downloadText } from './download-utils';
import type { ExportMenuItem } from './components/ExportMenuComponent';
import type { GoogleDriveConnectionState } from '../google-drive/google-drive-hooks';

/** Content for an export, resolved at click time so a live draft stays live. */
export type MarkdownSource = (setStatus: (status: string) => void) => Promise<string> | string;

/** Strip an extension and anything a filesystem or a header would object to. */
export function exportBaseName(name: string, fallback = 'document'): string {
  return name.replace(/\.[a-z0-9]{1,8}$/i, '').replace(/[^a-zA-Z0-9 _-]/g, '').trim() || fallback;
}

export function markdownDownloadItem(baseName: string, getMarkdown: MarkdownSource): ExportMenuItem {
  return {
    id: 'markdown',
    label: 'Markdown (.md)',
    run: async (setStatus) => {
      downloadText(`${exportBaseName(baseName)}.md`, await getMarkdown(setStatus), 'text/markdown;charset=utf-8');
    },
  };
}

/**
 * Quote a CSV field iff it contains a comma, a quote, or a newline; double inner
 * quotes. The only quoting rule every spreadsheet agrees on. Matches the
 * backend csv-xlsx renderer so a .csv downloaded here and a .xlsx built there
 * parse the same cells.
 */
function quoteCsvField(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/**
 * Pull every GFM table out of markdown and render each as a CSV block (header +
 * rows), joined by a blank line. Cells are unescaped (`\|` → `|`) and stripped
 * of inline emphasis/code markers — a paste into Excel reads the words, not the
 * asterisks. Returns '' when there is no table, so the caller can decide whether
 * to offer the item at all.
 */
function markdownTablesToCsv(markdown: string): string {
  const lines = markdown.split('\n');
  const blocks: string[] = [];
  // A table block is a pipe row, a separator row of dashes/colons, then every
  // following pipe row. Contiguity is what makes it one table.
  const isPipeRow = (s: string): boolean => /^\s*\|/.test(s) && /\|\s*$/.test(s);
  const isSeparator = (s: string): boolean =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(s);
  const parseRow = (s: string): string[] => {
    // Strip a leading/trailing pipe, split on the rest, unescape escaped pipes.
    const inner = s.replace(/^\s*\|/, '').replace(/\|\s*$/, '');
    return inner.split('|').map((cell) =>
      cell.trim().replace(/\\\|/g, '|').replace(/[*_`]/g, '').trim());
  };

  for (let i = 0; i < lines.length; i++) {
    if (!isPipeRow(lines[i]!) || !isSeparator(lines[i + 1] ?? '')) continue;
    const rows: string[][] = [parseRow(lines[i]!)];
    let j = i + 2;
    while (j < lines.length && isPipeRow(lines[j]!)) {
      rows.push(parseRow(lines[j]!));
      j++;
    }
    blocks.push(rows.map((r) => r.map(quoteCsvField).join(',')).join('\n'));
    i = j - 1;
  }
  return blocks.join('\n\n');
}

/**
 * CSV download of a document's markdown table(s). The mirror of the Excel item:
 * same table gate, same content source, just a plain-text grid instead of a
 * binary workbook. Client-side only — no backend route — because CSV is text.
 */
export function markdownCsvDownloadItem(
  baseName: string,
  getMarkdown: MarkdownSource,
  options?: { hint?: string; disabled?: boolean },
): ExportMenuItem {
  return {
    id: 'csv',
    label: 'CSV (.csv)',
    hint: options?.hint,
    disabled: options?.disabled,
    run: async (setStatus) => {
      const markdown = await getMarkdown(setStatus);
      const csv = markdownTablesToCsv(markdown);
      if (!csv) throw new Error('This document does not contain a table to export.');
      // A UTF-8 BOM so Excel detects the encoding and keeps non-ASCII intact —
      // the same reason the analytics CSV export prepends one.
      downloadText(`${exportBaseName(baseName)}.csv`, `\uFEFF${csv}`, 'text/csv;charset=utf-8');
    },
  };
}

export function wordDownloadItem(
  baseName: string,
  getMarkdown: MarkdownSource,
  options?: { hint?: string; disabled?: boolean },
): ExportMenuItem {
  return {
    id: 'docx',
    label: 'Word (.docx)',
    hint: options?.hint,
    disabled: options?.disabled,
    run: async (setStatus) => {
      const markdown = await getMarkdown(setStatus);
      setStatus('Building the .docx…');
      const fileName = exportBaseName(baseName);
      const result = await api.exportDocumentAsDocx({ markdown, fileName });
      if (!result.success) throw new Error(result.error.message);
      downloadBlob(`${fileName}.docx`, result.data);
    },
  };
}

/**
 * Excel export. Only meaningful when the markdown contains at least one table —
 * the server rejects table-free input, and a worksheet with no grid is the
 * thing a spreadsheet cannot handle. Callers that can tell should disable it;
 * the server is still the authority.
 */
export function excelDownloadItem(
  baseName: string,
  getMarkdown: MarkdownSource,
  options?: { hint?: string; disabled?: boolean },
): ExportMenuItem {
  return {
    id: 'xlsx',
    label: 'Excel (.xlsx)',
    hint: options?.hint,
    disabled: options?.disabled,
    run: async (setStatus) => {
      const markdown = await getMarkdown(setStatus);
      setStatus('Building the .xlsx…');
      const fileName = exportBaseName(baseName);
      const result = await api.exportDocumentAsXlsx({ markdown, fileName });
      if (!result.success) throw new Error(result.error.message);
      downloadBlob(`${fileName}.xlsx`, result.data);
    },
  };
}

/**
 * CSV → Excel. The chat agent writes `.csv` files because its file tools are
 * text-only; this item turns one into a real `.xlsx` on the download path so
 * the user does not have to Save-As themselves. `getCsv` resolves to the file's
 * text, the same source the "Download original (.csv)" item streams.
 */
export function csvExcelDownloadItem(
  baseName: string,
  getCsv: MarkdownSource,
  options?: { hint?: string; disabled?: boolean },
): ExportMenuItem {
  return {
    id: 'csv-xlsx',
    label: 'Excel (.xlsx)',
    hint: options?.hint,
    disabled: options?.disabled,
    run: async (setStatus) => {
      const csv = await getCsv(setStatus);
      setStatus('Building the .xlsx…');
      const fileName = exportBaseName(baseName);
      const result = await api.exportCsvAsXlsx({ csv, fileName });
      if (!result.success) throw new Error(result.error.message);
      downloadBlob(`${fileName}.xlsx`, result.data);
    },
  };
}

/**
 * "Publish as a draft on WordPress".
 *
 * Unlike its neighbours this one produces no file: it resolves the markdown the
 * same way they do — so an unsaved edit is saved first — and hands it to the
 * preview modal, where the user edits the title and HTML before anything is
 * created. Keeping it in the Download menu is deliberate: it is one more thing
 * to do with the document in front of you, and it used to sit beside the chat
 * composer where it acted on the last message instead.
 */
export function wordpressDraftItem(
  getMarkdown: MarkdownSource,
  onPrepared: (markdown: string) => void,
  options?: { hint?: string; disabled?: boolean },
): ExportMenuItem {
  return {
    id: 'wordpress',
    label: 'Publish as draft on WordPress…',
    hint: options?.hint ?? 'Opens a preview you can edit first',
    disabled: options?.disabled,
    run: async (setStatus) => {
      onPrepared(await getMarkdown(setStatus));
    },
  };
}

export interface DriveExportItemArgs {
  /** Markdown documents. Mutually exclusive with `artifactId`. */
  title?: string;
  getMarkdown?: MarkdownSource;
  /** Slide decks — the server renders the .pptx and Drive converts it to Slides. */
  artifactId?: string;
  connection: GoogleDriveConnectionState;
  /** Where the consent screen should return to. */
  returnTo: string;
  hint?: string;
  disabled?: boolean;
}

/**
 * "Export a copy to my Drive", or "Connect Google Drive…" when they have not yet.
 *
 * The unconnected case is a step in the flow, not an error: clicking it starts
 * consent and comes back to the same page. Returns null when the feature flag is
 * off, so the caller can spread it away rather than render a dead item.
 */
export function driveExportItem(args: DriveExportItemArgs): ExportMenuItem | null {
  const { connection } = args;
  if (!connection.exportEnabled) return null;

  if (!connection.connected) {
    return {
      id: 'drive',
      label: 'Connect Google Drive…',
      hint: connection.loading ? 'Checking…' : "One-time sign-in, then we'll put a copy in your Drive",
      disabled: connection.loading,
      run: async () => { connection.startConnect(args.returnTo); },
    };
  }

  return {
    id: 'drive',
    label: 'Export a copy to my Drive',
    hint: args.hint ?? (connection.googleUserEmail ?? undefined),
    disabled: args.disabled,
    run: async (setStatus) => {
      // Opened synchronously inside the click. Anything after an await is a
      // popup as far as the browser is concerned, and gets blocked.
      const tab = window.open('about:blank', '_blank', 'noopener');
      try {
        let body: DocumentExportDriveRequest;
        if (args.artifactId) {
          body = { source: 'artifactDeck', artifactId: args.artifactId };
        } else {
          if (!args.getMarkdown || !args.title) {
            throw new Error('Nothing to export.');
          }
          const markdown = await args.getMarkdown(setStatus);
          body = { source: 'markdown', markdown, title: exportBaseName(args.title) };
        }

        setStatus('Uploading to Google Drive…');
        const result = await api.exportDocumentToDrive(body);
        if (!result.success) throw new Error(result.error.message);

        if (tab) {
          tab.location.href = result.data.webViewLink;
        } else {
          // The blocker won. Surfacing the link beats appearing to do nothing.
          throw new Error(`Your browser blocked the new tab. The copy is in your Drive: ${result.data.webViewLink}`);
        }
      } catch (error) {
        tab?.close();
        throw error;
      }
    },
  };
}
